import { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { Badge, Box, Flex, Spinner, Text, useDisclosure } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import {
  DropdownMultiSelectInput,
  formatCurrency,
  formatNumber,
  PointsTransaction,
  PointsTransactionReason,
  Recognition,
  individualsService,
} from '@aslanpay/portal-common-lib';

import EmptyState from 'app/components/UI/EmptyState';
import useInfiniteFetchPointsTransactions from 'app/hooks/useInfiniteFetchPointsTransactions';
import useInfiniteFetchRecognitionsByGroup from 'app/hooks/useInfiniteFetchRecognitionsByGroup';
import { getPointsValue } from 'app/utils/points';

import PotRecognitionDetailsModal from './PotRecognitionDetailsModal';

interface PotActivityFeedProps {
  potId: string;
  potName: string;
  groupId?: string;
}

const FILTER_OPTIONS = [
  { value: 'funds_added', text: 'Funds added' },
  { value: 'points_transferred', text: 'Points transferred' },
  { value: 'recognition_with_points', text: 'Recognition with points' },
  { value: 'recognition_without_points', text: 'Recognition without points' },
  { value: 'recognition_approved', text: 'Recognition approved' },
  { value: 'recognition_declined', text: 'Recognition declined' },
];

const FUNDS_ADDED_REASONS: PointsTransactionReason[] = ['FUNDS_TOP_UP'];
const POINTS_TRANSFERRED_REASONS: PointsTransactionReason[] = ['FUNDS_TRANSFER'];

const REASON_LABELS: Partial<Record<PointsTransactionReason, string>> = {
  FUNDS_TOP_UP: 'Funds added',
  FUNDS_TRANSFER: 'Points transferred',
  COMPANY_TOP_UP: 'Funds added',
  EMPLOYEE_GIFT: 'Employee gift',
  GOODWILL: 'Goodwill',
  EMPLOYEE_TOP_UP: 'Employee top-up',
  PURCHASE: 'Purchase',
  REFERRAL: 'Referral',
  DIGITAL_VOUCHER: 'Digital voucher',
  RECOGNITION: 'Recognition',
  OTHER: 'Other',
};

export function getReasonLabel(reason?: PointsTransactionReason): string {
  if (!reason) return 'Activity';
  return REASON_LABELS[reason] ?? reason;
}

export function filterNonRecognitionTransactions(
  transactions: PointsTransaction[]
): PointsTransaction[] {
  return transactions.filter((tx) => tx.transactionReason !== 'RECOGNITION');
}

function getFilterReasons(selected: string[]): PointsTransactionReason[] | undefined {
  if (selected.length === 0) return undefined;
  const reasons: PointsTransactionReason[] = [];
  if (selected.includes('funds_added')) reasons.push(...FUNDS_ADDED_REASONS);
  if (selected.includes('points_transferred')) reasons.push(...POINTS_TRANSFERRED_REASONS);
  return reasons.length > 0 ? reasons : undefined;
}

function getDisplayLabel(selected: string[]) {
  const label = (() => {
    if (selected.length === 0) return 'All Activity';
    const first = FILTER_OPTIONS.find((o) => o.value === selected[0])?.text ?? '';
    if (selected.length === 1) return first;
    return `${first}, +${selected.length - 1}`;
  })();

  return (
    <>
      <Text as="span" fontWeight="700" fontSize="14px">
        Showing:{' '}
      </Text>
      <Text as="span" fontSize="14px">
        {label}
      </Text>
    </>
  );
}

function B({ children }: { children: ReactNode }) {
  return (
    <Text as="span" fontWeight="bold">
      {children}
    </Text>
  );
}

export function buildTransactionDescription(
  transaction: PointsTransaction,
  actorName: string,
  potName: string
): ReactNode {
  const points = formatNumber(transaction.amount);
  const value = formatCurrency(getPointsValue(transaction.amount));
  if (transaction.transactionReason === 'FUNDS_TOP_UP') {
    return (
      <>
        <B>{actorName}</B> added <B>{points} points</B> (worth {value})
      </>
    );
  }
  if (transaction.transactionReason === 'FUNDS_TRANSFER') {
    return (
      <>
        <B>{actorName}</B> transferred <B>{points} points</B> (worth {value}) to the {potName} pot
      </>
    );
  }
  return transaction.labelName ?? transaction.notes ?? '-';
}

function ActivityCard({
  transaction,
  actorName,
  potName,
}: {
  transaction: PointsTransaction;
  actorName: string;
  potName: string;
}) {
  const date = parseISO(transaction.createdAt);
  const label = getReasonLabel(transaction.transactionReason);
  const description = buildTransactionDescription(transaction, actorName, potName);

  return (
    <Flex
      w="100%"
      maxW="100%"
      minW={0}
      h="133px"
      bg="brand.lightGray2"
      border="1px solid"
      borderColor="brand.lightGray1"
      borderRadius="5px"
      p="10px 20px"
      flexDir="column"
      justify="space-between"
      overflow="hidden"
      flexShrink={1}
    >
      <Flex justify="space-between" align="center">
        <Badge colorScheme="badge.invited" fontSize="12px">
          {label}
        </Badge>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {format(date, 'dd/MM/yy HH:mm')}
        </Text>
      </Flex>

      <Text
        textStyle="smallRegular"
        color="brand.black"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {description}
      </Text>

      <Text fontSize="14px" fontWeight="400" color="brand.darkGray">
        {format(date, 'dd/MM/yyyy HH:mm')}
      </Text>
    </Flex>
  );
}

function RecognitionActivityCard({
  recognition,
  onOpenDetails,
}: {
  recognition: Recognition;
  onOpenDetails: () => void;
}) {
  const date = parseISO(recognition.submittedAt);
  const senderName = `${recognition.senderFirstName} ${recognition.senderLastName}`;
  const receiverName = `${recognition.receiverFirstName} ${recognition.receiverLastName}`;
  const description = recognition.hasPoints ? (
    <>
      <B>{senderName}</B> recognised <B>{receiverName}</B> with{' '}
      <B>{formatNumber(recognition.points ?? 0)} points</B> (worth{' '}
      {formatCurrency(getPointsValue(recognition.points ?? 0))})
    </>
  ) : (
    <>
      <B>{senderName}</B> recognised <B>{receiverName}</B>
    </>
  );

  return (
    <Flex
      w="100%"
      maxW="100%"
      minW={0}
      h="133px"
      bg="brand.lightGray2"
      border="1px solid"
      borderColor="brand.lightGray1"
      borderRadius="5px"
      p="10px 20px"
      flexDir="column"
      justify="space-between"
      overflow="hidden"
      flexShrink={1}
    >
      <Flex justify="space-between" align="center">
        <Badge colorScheme="badge.invited" fontSize="12px">
          {recognition.hasPoints ? 'Recognition with points' : 'Recognition without points'}
        </Badge>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {format(date, 'dd/MM/yy HH:mm')}
        </Text>
      </Flex>

      <Text
        textStyle="smallRegular"
        color="brand.black"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {description}
      </Text>

      <Text
        textStyle="smallMedium"
        fontSize="14px"
        color="brand.purple"
        cursor="pointer"
        _hover={{ textDecor: 'underline' }}
        onClick={onOpenDetails}
      >
        Open details
      </Text>
    </Flex>
  );
}

type RecognitionDecision = {
  recognition: Recognition;
  decision: 'APPROVED' | 'REJECTED';
  actorId: string;
  occurredAt: string;
};

type FeedItem =
  | { type: 'transaction'; timestamp: number; data: PointsTransaction }
  | { type: 'recognition'; timestamp: number; data: Recognition }
  | { type: 'decision'; timestamp: number; data: RecognitionDecision };

function RecognitionDecisionCard({
  decision,
  actorName,
}: {
  decision: RecognitionDecision;
  actorName: string;
}) {
  const date = parseISO(decision.occurredAt);
  const senderName = `${decision.recognition.senderFirstName} ${decision.recognition.senderLastName}`;
  const receiverName = `${decision.recognition.receiverFirstName} ${decision.recognition.receiverLastName}`;
  const isApproved = decision.decision === 'APPROVED';
  const label = isApproved ? 'Recognition approved' : 'Recognition declined';
  const description = isApproved ? (
    <>
      <B>{actorName}</B> approved <B>{senderName}</B>&apos;s recognition of <B>{receiverName}</B>
    </>
  ) : (
    <>
      <B>{actorName}</B> declined <B>{senderName}</B>&apos;s recognition of <B>{receiverName}</B>
      {decision.recognition.rejectionReason ? ` — ${decision.recognition.rejectionReason}` : ''}
    </>
  );

  return (
    <Flex
      w="100%"
      maxW="100%"
      minW={0}
      h="133px"
      bg="brand.lightGray2"
      border="1px solid"
      borderColor="brand.lightGray1"
      borderRadius="5px"
      p="10px 20px"
      flexDir="column"
      justify="space-between"
      overflow="hidden"
      flexShrink={1}
    >
      <Flex justify="space-between" align="center">
        <Badge colorScheme={isApproved ? 'badge.active' : 'badge.left'} fontSize="12px">
          {label}
        </Badge>
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {format(date, 'dd/MM/yy HH:mm')}
        </Text>
      </Flex>

      <Text
        textStyle="smallRegular"
        color="brand.black"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {description}
      </Text>

      <Text fontSize="14px" fontWeight="400" color="brand.darkGray">
        {format(date, 'dd/MM/yyyy HH:mm')}
      </Text>
    </Flex>
  );
}

function PotActivityFeed({ potId, potName, groupId = undefined }: PotActivityFeedProps) {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedRecognition, setSelectedRecognition] = useState<Recognition | undefined>();
  const { isOpen: isDetailsOpen, onOpen: openDetails, onClose: closeDetails } = useDisclosure();
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const transactionFilterSelected =
    selectedFilters.length === 0 ||
    selectedFilters.includes('funds_added') ||
    selectedFilters.includes('points_transferred');

  const recognitionFilterSelected =
    selectedFilters.length === 0 ||
    selectedFilters.includes('recognition_with_points') ||
    selectedFilters.includes('recognition_without_points');

  const decisionFilterSelected =
    selectedFilters.length === 0 ||
    selectedFilters.includes('recognition_approved') ||
    selectedFilters.includes('recognition_declined');

  const needsRecognitions = recognitionFilterSelected || decisionFilterSelected;

  const reasons = getFilterReasons(selectedFilters);

  const {
    data: transactionData,
    isLoading: isLoadingTransactions,
    isFetchingNextPage: isFetchingNextTransactions,
    hasNextPage: hasNextTransactions,
    fetchNextPage: fetchNextTransactions,
  } = useInfiniteFetchPointsTransactions(
    {
      potIds: [potId],
      reasons,
      sort: 'DESC',
      sortBy: 'createdAt',
      size: 20,
    },
    !!potId && transactionFilterSelected
  );

  const {
    data: recognitionData,
    isLoading: isLoadingRecognitions,
    isFetchingNextPage: isFetchingNextRecognitions,
    hasNextPage: hasNextRecognitions,
    fetchNextPage: fetchNextRecognitions,
  } = useInfiniteFetchRecognitionsByGroup(groupId ?? '', 20, !!groupId && needsRecognitions, [
    'APPROVED',
    'REJECTED',
  ]);

  const transactions = useMemo(
    () => transactionData?.pages.flatMap((p) => p.list) ?? [],
    [transactionData]
  );
  const recognitions = useMemo(
    () => recognitionData?.pages.flatMap((p) => p.list) ?? [],
    [recognitionData]
  );

  const actorIds = useMemo(() => {
    const ids = new Set<string>();
    transactions.forEach((t) => {
      if (t.createdBy) ids.add(t.createdBy);
    });
    recognitions.forEach((r) => {
      if (r.approvedBy) ids.add(r.approvedBy);
      if (r.rejectedBy) ids.add(r.rejectedBy);
    });
    return [...ids];
  }, [transactions, recognitions]);

  const { data: individualsData } = useQuery({
    queryKey: ['individuals', actorIds],
    queryFn: () =>
      individualsService.fetchIndividuals({
        ids: actorIds,
        page: 0,
        size: actorIds.length,
      }),
    enabled: actorIds.length > 0,
  });

  const actorNamesById = useMemo(
    () => new Map(individualsData?.list.map((i) => [i.id, `${i.firstName} ${i.lastName}`]) ?? []),
    [individualsData]
  );

  const filteredRecognitions = useMemo(() => {
    const approved = recognitions.filter((r) => r.status === 'APPROVED');
    if (selectedFilters.length === 0) return approved;
    const withPoints = selectedFilters.includes('recognition_with_points');
    const withoutPoints = selectedFilters.includes('recognition_without_points');
    if (withPoints && withoutPoints) return approved;
    if (withPoints) return approved.filter((r) => r.hasPoints);
    if (withoutPoints) return approved.filter((r) => !r.hasPoints);
    return [];
  }, [recognitions, selectedFilters]);

  const decisionItems = useMemo<RecognitionDecision[]>(() => {
    const items: RecognitionDecision[] = [];
    recognitions.forEach((r) => {
      if (r.approvedAt && r.approvedBy) {
        items.push({
          recognition: r,
          decision: 'APPROVED',
          actorId: r.approvedBy,
          occurredAt: r.approvedAt,
        });
      }
      if (r.rejectedAt && r.rejectedBy) {
        items.push({
          recognition: r,
          decision: 'REJECTED',
          actorId: r.rejectedBy,
          occurredAt: r.rejectedAt,
        });
      }
    });
    return items;
  }, [recognitions]);

  const filteredDecisions = useMemo(() => {
    if (selectedFilters.length === 0) return decisionItems;
    const showApproved = selectedFilters.includes('recognition_approved');
    const showDeclined = selectedFilters.includes('recognition_declined');
    if (!showApproved && !showDeclined) return [];
    return decisionItems.filter(
      (d) =>
        (showApproved && d.decision === 'APPROVED') || (showDeclined && d.decision === 'REJECTED')
    );
  }, [decisionItems, selectedFilters]);

  const feedItems = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = [];

    if (transactionFilterSelected) {
      filterNonRecognitionTransactions(transactions).forEach((tx) =>
        items.push({ type: 'transaction', timestamp: parseISO(tx.createdAt).getTime(), data: tx })
      );
    }

    if (recognitionFilterSelected) {
      filteredRecognitions.forEach((r) =>
        items.push({ type: 'recognition', timestamp: parseISO(r.submittedAt).getTime(), data: r })
      );
    }

    if (decisionFilterSelected) {
      filteredDecisions.forEach((d) =>
        items.push({ type: 'decision', timestamp: parseISO(d.occurredAt).getTime(), data: d })
      );
    }

    return items.sort((a, b) => b.timestamp - a.timestamp);
  }, [
    transactions,
    filteredRecognitions,
    filteredDecisions,
    transactionFilterSelected,
    recognitionFilterSelected,
    decisionFilterSelected,
  ]);

  const isLoading = isLoadingTransactions || isLoadingRecognitions;
  const isFetchingNextPage = isFetchingNextTransactions || isFetchingNextRecognitions;
  const hasNextPage = hasNextTransactions || hasNextRecognitions;

  useEffect(() => {
    const el = loadMoreRef.current;
    const root = scrollContainerRef.current;
    if (!el || !root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          if (hasNextTransactions) fetchNextTransactions();
          if (hasNextRecognitions) fetchNextRecognitions();
        }
      },
      { root, threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    hasNextPage,
    isFetchingNextPage,
    hasNextTransactions,
    hasNextRecognitions,
    fetchNextTransactions,
    fetchNextRecognitions,
  ]);

  return (
    <Flex flexDir="column" gap="16px" w="100%" minW={0}>
      <Box
        w="348px"
        sx={{ '& input:not([data-testid="dropdown-search-input"])': { height: '50px' } }}
      >
        <DropdownMultiSelectInput
          options={FILTER_OPTIONS}
          selected={selectedFilters}
          onChange={setSelectedFilters}
          placeholder="Showing: All Activity"
          renderCustomDisplayLabel={getDisplayLabel}
          isSearchable
        />
      </Box>

      <Box ref={scrollContainerRef} maxH="600px" overflowY="auto" w="100%" minW={0}>
        {isLoading && (
          <Flex justify="center" py="40px">
            <Spinner color="brand.purple" />
          </Flex>
        )}

        {!isLoading && feedItems.length === 0 && <EmptyState text="No activity found" />}

        <Box display="grid" gridTemplateColumns="minmax(0, 1fr)" gap="12px" w="100%">
          {feedItems.map((item) => {
            if (item.type === 'transaction') {
              return (
                <ActivityCard
                  key={`tx-${item.data.id}`}
                  transaction={item.data}
                  actorName={actorNamesById.get(item.data.createdBy) ?? '—'}
                  potName={potName}
                />
              );
            }
            if (item.type === 'recognition') {
              return (
                <RecognitionActivityCard
                  key={`rec-${item.data.id}`}
                  recognition={item.data}
                  onOpenDetails={() => {
                    setSelectedRecognition(item.data);
                    openDetails();
                  }}
                />
              );
            }
            return (
              <RecognitionDecisionCard
                key={`dec-${item.data.decision}-${item.data.recognition.id}`}
                decision={item.data}
                actorName={actorNamesById.get(item.data.actorId) ?? '—'}
              />
            );
          })}
        </Box>

        <div ref={loadMoreRef} />

        {isFetchingNextPage && (
          <Flex justify="center" py="16px">
            <Spinner size="sm" color="brand.purple" />
          </Flex>
        )}
      </Box>

      {selectedRecognition && (
        <PotRecognitionDetailsModal
          isOpen={isDetailsOpen}
          onClose={closeDetails}
          recognition={selectedRecognition}
        />
      )}
    </Flex>
  );
}

export default PotActivityFeed;
